import { Config } from '@abiglobalhealth/go-types'
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useEffect, useRef, useState } from 'react'

import { getStoredConfig } from '@/utils/config'
import { getThemeBackgroundPattern } from '@/utils/theme/backgroundPattern'

export interface ConfigContextProps {
  config: Config | undefined
  setConfig: Dispatch<SetStateAction<Config | undefined>>
}

export const ConfigContext = createContext({} as ConfigContextProps)

export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const [config, setConfig] = useState<ConfigContextProps['config']>(getStoredConfig)

  const customThemeAttributes = useRef<Record<string, string>>({})

  const setAttribute = (prev: string, key: string, value: string) => {
    const attribute = `${prev}-${key}`
    customThemeAttributes.current[attribute] = value
  }

  const parseAttributes = (theme: any, prev: string) => {
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === 'string') {
        setAttribute(prev, key, value)
      } else {
        // Recursively parse attributes
        parseAttributes(value, `${prev}-${key}`)
      }
    })
  }

  useEffect(() => {
    // Reset custom theme
    customThemeAttributes.current = {}
    const styleTag = document.getElementById('custom-theme')
    styleTag?.remove()

    // Parse theme attributes
    if (config?.theme) parseAttributes(config.theme, '--theme')
    customThemeAttributes.current['--theme-background-pattern'] = getThemeBackgroundPattern(config?.theme)

    // Create custom theme style tag and append to head
    const style = document.createElement('style')
    style.id = 'custom-theme'
    style.innerHTML = `:root{${Object.entries(customThemeAttributes.current)
      .map(([key, value]) => `${key}:${value};`)
      .join('')}}`
    document.head.appendChild(style)
  }, [config?.theme])

  useEffect(() => {
    if (!config?.meta) return

    const title = config.meta.title
    const description = config.meta.description

    if (title) document.title = title
    if (description) document.querySelector('meta[name="description"]')?.setAttribute('content', description)
  }, [config?.meta])

  const value = {
    config,
    setConfig
  }

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}
